/**
 * Pace Projector Engine (Phase 3 — Step 3.4).
 *
 * Projects attendance pace: miss rate, projected total, sessions per absence.
 */
import { computeEffectiveAbsences, computeCreditHourDeduction } from './budgetCalculator';
import { resolveActiveTier, computeTotalDeduction, resolveLetterGrade } from './penaltyResolver';
import { getPastSessionCount } from './sessionSchedule';
import { todayISO } from './utils/dates';
/**
 * Project the student's attendance pace based on current absence rate.
 */
export function projectPace(entries, policy, sessions) {
    const pastCount = getPastSessionCount(sessions);
    const totalSessions = sessions.length;
    const remainingSessions = totalSessions - pastCount;
    const effectiveAbsences = computeEffectiveAbsences(entries, policy);
    const penaltyAbsences = effectiveAbsences.totalPenaltyAbsences;
    // Miss rate based on past sessions
    const missRate = pastCount > 0 ? penaltyAbsences / pastCount : 0;
    // Project total at end of term
    const projectedTotal = Math.round(missRate * totalSessions);
    // Check if projected leads to auto-fail
    const { activeTier: _activeTier } = resolveActiveTier(policy.tiers, projectedTotal);
    const maxAllowed = policy.attendanceFloor;
    let leadsToFail = false;
    if (policy.attendanceFloorUnit === 'count') {
        leadsToFail = projectedTotal >= maxAllowed;
    }
    else {
        // Percentage-based
        const projectedAttended = totalSessions - projectedTotal;
        const projectedPct = totalSessions > 0 ? (projectedAttended / totalSessions) * 100 : 100;
        leadsToFail = projectedPct <= maxAllowed;
    }
    // Sessions per absence
    const sessionsPerAbsence = missRate > 0 ? Math.round(1 / missRate * 10) / 10 : Infinity;
    return {
        missRate: Math.round(missRate * 1000) / 1000,
        projectedTotal,
        leadsToFail,
        sessionsPerAbsence,
        pastSessions: pastCount,
        remainingSessions,
    };
}
/**
 * Project the date at which the student reaches the auto-fail floor
 * given their current miss rate and remaining absence budget.
 *
 * @param sessions - All scheduled sessions (past + future)
 * @param missRate - Current miss rate (0.0 to 1.0)
 * @param maxAbsences - Maximum absences allowed before auto-fail
 * @param usedAbsences - Penalty absences already used
 * @returns autoFailDate (ISO) and sessionsUntilAutoFail (Infinity → null date)
 */
export function projectAutoFailDate(sessions, missRate, maxAbsences, usedAbsences) {
    const absencesRemaining = Math.max(0, maxAbsences - usedAbsences);
    const sessionsUntilAutoFail = missRate > 0 ? Math.ceil(absencesRemaining / missRate) : Infinity;
    if (sessionsUntilAutoFail === Infinity) {
        return { autoFailDate: null, sessionsUntilAutoFail };
    }
    const today = todayISO();
    const futureSessions = sessions
        .filter((s) => s.date > today)
        .sort((a, b) => a.date.localeCompare(b.date));
    // sessionsUntilAutoFail is 1-indexed count: the Nth future session
    const target = futureSessions[sessionsUntilAutoFail - 1];
    if (!target) {
        return { autoFailDate: null, sessionsUntilAutoFail };
    }
    return { autoFailDate: target.date, sessionsUntilAutoFail };
}
/**
 * Project the final letter grade if the current miss-rate pace holds
 * to the end of term.
 *
 * @param baseGrade - Current base grade (null = no projection)
 * @param gradingScale - Letter grade thresholds
 * @param policy - Attendance policy (for deduction computation)
 * @param projectedTotalAbsences - Projected end-of-term penalty absences
 * @param creditHours - Course credit hours for weighting
 */
export function projectFinalGradeAtPace(baseGrade, gradingScale, policy, projectedTotalAbsences, creditHours) {
    if (baseGrade === null) {
        return { projectedFinalGrade: null, projectedFinalPoints: null };
    }
    const eff = {
        totalPenaltyAbsences: projectedTotalAbsences,
        effectiveUnexcused: 0,
        countedExcused: 0,
        tardyConversions: 0,
        remainingTardies: 0,
    };
    const deduction = computeCreditHourDeduction(computeTotalDeduction(policy, eff), creditHours);
    const points = Math.max(0, baseGrade - deduction);
    const grade = resolveLetterGrade(points, gradingScale);
    return { projectedFinalGrade: grade, projectedFinalPoints: points };
}
//# sourceMappingURL=paceProjector.js.map